import {
  Button, Checkbox,
  FilterPillButton,
  FiltersDropdown
} from 'slab'
import { type MemberOnboardingMedicalPlanDisplayItem } from 'utils/web_api.schemas'

export interface PlanFiltersState {
  metalTiers: string[]
  carriers: string[]
  planTypes: string[]
  onlyFullyCovered: boolean
}

interface PlanFiltersProps {
  plans: MemberOnboardingMedicalPlanDisplayItem[]
  filters: PlanFiltersState
  setFilters: (filters: PlanFiltersState) => void
}

const metalTierOrder = [
  'expanded_bronze',
  'bronze',
  'silver',
  'gold',
  'platinum'
]

const metalTierSort = (a: string, b: string) => {
  const indexA = metalTierOrder.indexOf(a)
  const indexB = metalTierOrder.indexOf(b)

  // If both are in the order list, sort by their order
  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB
  }
  // If only a is in the list, it comes first
  if (indexA !== -1) {
    return -1
  }
  // If only b is in the list, it comes first
  if (indexB !== -1) {
    return 1
  }
  // Otherwise, sort alphabetically
  return a.localeCompare(b)
}

const formatMetalTier = (tier: string) => {
  if (tier === 'expanded_bronze') {
    return 'Expanded bronze'
  }
  return tier.charAt(0).toUpperCase() + tier.slice(1)
}

export const EmployeePlanFilters = ({ plans, filters, setFilters }: PlanFiltersProps) => {
  // Extract unique values from plans
  const uniqueMetalTiers = [...new Set(plans.map(plan => plan.plan.metal_tier))].sort(metalTierSort)
  const uniqueCarriers = [...new Set(plans.map(plan => plan.plan.carrier_name))].sort()
  const uniquePlanTypes = [...new Set(plans.map(plan => plan.plan.plan_type))].sort()

  const toggle = (
    filterType: keyof PlanFiltersState,
    filter: string
  ) => {
    return () => {
      const newFilters = { ...filters }

      if (filterType === 'metalTiers') {
        newFilters.metalTiers = filters.metalTiers.includes(filter)
          ? filters.metalTiers.filter((f) => f !== filter)
          : [...filters.metalTiers, filter]
      } else if (filterType === 'planTypes') {
        newFilters.planTypes = filters.planTypes.includes(filter)
          ? filters.planTypes.filter((f) => f !== filter)
          : [...filters.planTypes, filter]
      } else if (filterType === 'carriers') {
        newFilters.carriers = filters.carriers.includes(filter)
          ? filters.carriers.filter((f) => f !== filter)
          : [...filters.carriers, filter]
      } else if (filterType === 'onlyFullyCovered') {
        newFilters.onlyFullyCovered = !filters.onlyFullyCovered
      }

      setFilters(newFilters)
    }
  }

  const onClearFilters = () => {
    setFilters({
      metalTiers: [],
      carriers: [],
      planTypes: [],
      onlyFullyCovered: false,
    })
  }

  return (
    <div className='flex justify-start gap-2 -mx-6 mb-6 px-6 py-0.5 flex-nowrap overflow-auto'>
      <FiltersDropdown
        withPortal={false}
        zIndex={1}
        trigger={
          <FilterPillButton
            label='Metal tier'
            count={filters.metalTiers.length}
          />
        }
      >
        <FiltersDropdown.Filters>
          {uniqueMetalTiers.map((tier) => (
            <Checkbox
              key={tier}
              checked={filters.metalTiers.includes(tier)}
              onChange={toggle('metalTiers', tier)}
              value={tier}
              label={formatMetalTier(tier)}
            />
          ))}
        </FiltersDropdown.Filters>
      </FiltersDropdown>
      <FiltersDropdown
        withPortal={false}
        zIndex={1}
        trigger={
          <FilterPillButton
            label='Insurance company'
            count={filters.carriers.length}
          />
        }
      >
        <FiltersDropdown.Filters>
          {uniqueCarriers.map((carrier) => (
            <Checkbox
              key={carrier}
              checked={filters.carriers.includes(carrier)}
              onChange={toggle('carriers', carrier)}
              value={carrier}
              label={carrier}
            />
          ))}
        </FiltersDropdown.Filters>
      </FiltersDropdown>
      <FiltersDropdown
        withPortal={false}
        zIndex={1}
        trigger={
          <FilterPillButton
            label='Plan type'
            count={filters.planTypes.length}
          />
        }
      >
        <FiltersDropdown.Filters>
          {uniquePlanTypes.map((planType) => (
            <Checkbox
              key={planType}
              checked={filters.planTypes.includes(planType)}
              onChange={toggle('planTypes', planType)}
              value={planType}
              label={planType.toUpperCase()}
            />
          ))}
        </FiltersDropdown.Filters>
      </FiltersDropdown>
      <FilterPillButton
        label='Only show fully covered plans'
        count={filters.onlyFullyCovered ? 1 : 0}
        onClick={toggle('onlyFullyCovered', 'onlyFullyCovered')}
        hideChevron
      />
      {(filters.metalTiers.length > 0 ||
        filters.carriers.length > 0 ||
        filters.planTypes.length > 0 ||
        filters.onlyFullyCovered) && (
          <Button size='small' variant='tertiary-deemphasized' onClick={onClearFilters}>
            Reset filters
          </Button>
      )}
    </div>
  )
}
